/**
 * 云服务类别 产品图表页 图表详情Dialog
 *
 * @format
 */

import React, { useEffect, useMemo, useState } from 'react'
import { _$, Button, Dialog, Select, Option, OptionGroup } from '@king-design/react'
import dayjs from 'dayjs'
import styled from 'styled-components'
import NoDataComponent from 'src/components/NoData'
import QuickRangeDate from 'src/components/QuickRangeDate'
import { EchartReact } from 'src/components'
import { ebs_childtypes } from 'src/containers/MonitorCloud/MonitorCloudChart/MonitorChartList'
import { request, tooltipFormatter, handleUnit } from 'src/utils'
import {
  calcDateFormat,
  IChartDataProps,
  calcSelectInterval,
  getQueryDataParams,
  AggregatorEnum,
  AggregatorKeys
} from '../constants'
import API from 'src/api/ApiCloud'
import { cloneDeep } from 'lodash'
import { stringNumberSort } from 'src/utils/common'
import { echartTooltipOptions } from 'src/utils/echarts'
interface IDiabolChartDetailsProps {
  visible: boolean
  region: string
  nameSpace: string
  productType: number
  graph: {
    instanceId?: string
    graphId?: number
    units?: string
    title?: string
    aggregate?: AggregatorKeys
    tags?: any[]
  }
  closeDetails: () => void
  rangeDate: [number, number]
  quickDate: string
  mointorClassData: any[]
  className?: string
}

const DiabolChartDetails: React.FC<IDiabolChartDetailsProps> = ({
  visible,
  region,
  nameSpace,
  productType,
  graph,
  closeDetails,
  rangeDate: rangeDateProp,
  quickDate: quickDateProp,
  mointorClassData,
  className
}) => {
  const [monitorItemValue, setMonitorItemValue] = useState<string>()
  const [quickDate, setQuickDate] = useState<string>()
  const [rangeDate, setRangeDate] = useState([dayjs().subtract(30, 'm').unix(), dayjs().unix()])
  const [aggregate, setAggregate] = useState<AggregatorKeys>('max')
  const [delayOptions, setDelayOptions] = useState<string[]>([])
  const [delayValue, setDelayValue] = useState<string>('')
  const [currentGraph, setCurrentGraph] = useState<any>({})
  const [chartData, setChartData] = useState<IChartDataProps>()
  const [tags, setTags] = useState<string[]>([])

  useEffect(() => {
    setTags(graph?.tags || [])
  }, [graph?.tags])

  const appTags = useMemo(() => {
    if (currentGraph) {
      const result = []
      currentGraph?.lines?.forEach(({ tags, metricType }) => {
        if (metricType === 1 && tags?.p1 && !result.includes(tags?.p1)) {
          result.push(tags?.p1)
        }
      })
      setTags(tags => {
        return tags?.filter(tag => result.includes(tag)) || []
      })
      return result.sort(stringNumberSort)
    } else {
      return []
    }
  }, [currentGraph])

  // 初始化
  useEffect(() => {
    if (visible && graph.graphId !== undefined && graph.title) {
      setMonitorItemValue(`${graph.graphId}-${graph.title}`)
      setAggregate(graph.aggregate)
      if (quickDateProp) {
        setQuickDate(quickDateProp)
      }
      rangeDateChange(rangeDateProp)
    } else {
      setQuickDate(undefined)
      setRangeDate([])
      setAggregate('max')
      setMonitorItemValue(undefined)
      setDelayOptions([])
      setDelayValue('')
      setCurrentGraph(undefined)
      setChartData(undefined)
    }
  }, [visible, graph.graphId, graph.aggregate])

  // 当监控项变化时请求数据
  useEffect(() => {
    if (monitorItemValue !== undefined) {
      for (const index in mointorClassData) {
        const { graphs } = mointorClassData[index]
        const current = graphs.find(
          ({ graphId, title }) => `${graphId}-${title}` === monitorItemValue
        )
        if (current) {
          setCurrentGraph(current)
          setDelayOption(current)
          break
        }
      }
    }
  }, [monitorItemValue])

  // 切换日期范围改变粒度
  const rangeDateChange = (date, quick?) => {
    const [start, end] = date
    if (start && end) {
      setDelayOption(currentGraph, date)
      setQuickDate(quick)
      setRangeDate(date)
    }
  }

  // 设置粒度
  const setDelayOption = (
    graph = currentGraph,
    date = rangeDate?.length ? rangeDate : rangeDateProp
  ) => {
    const [start, end] = date
    if (graph?.lines[0]?.delay && start && end) {
      const options = calcSelectInterval(date, graph?.lines[0]?.delay)
      setDelayOptions(options)
      const findDelay = options.includes(delayValue)
      if (!findDelay) {
        setDelayValue(options[0])
      }
    }
  }

  // 日期/监控项改变时发送请求
  useEffect(() => {
    const [start, end] = rangeDate
    if (visible && start && end && currentGraph?.lines) {
      getChartData()
    } else {
      setChartData(undefined)
    }
  }, [rangeDate, currentGraph?.lines])

  // 获取chartData数据
  const getChartData = async (data?: any, newTags = tags) => {
    // 维度筛选
    let cloneLines = cloneDeep(currentGraph.lines)
    if (newTags?.length) {
      cloneLines = cloneLines.filter(line => {
        return line.metricType !== 1 || newTags.includes(line.tags?.p1)
      })
    }
    const params = getQueryDataParams(
      {
        ...currentGraph,
        lines: cloneLines
      },
      graph.instanceId,
      rangeDate,
      productType,
      data?.delay || delayValue
    )
    const result = await request(API.getMetricsData, {
      method: 'POST',
      data: {
        ...params,
        Namespace: ebs_childtypes.includes(productType) ? 'DBSC/EBS' : nameSpace,
        Aggregate: data?.aggregate || aggregate
      },
      region
    })
    if (result?.data?.data?.length) {
      const xData = result?.data?.data?.[0]?.datapoints?.map(({ timestamp }) => {
        return dayjs(timestamp * 1000).format(calcDateFormat(rangeDate, params.Period))
      })
      const series = []
      let maxValue = 0
      let minValue = 0
      result?.data?.data?.forEach(item => {
        const { datapoints, metricDesc } = item
        const symbol = datapoints.find(({ value }) => value === null)
        const seriesObject = {
          name: metricDesc,
          type: 'line',
          smooth: true,
          symbol: datapoints.length > 5 && !symbol ? 'none' : 'emptyCircle',
          data: datapoints.map(({ value }) => value),
          showSymbol: false,
          lineStyle: {
            width: 2
          }
        }
        // 取最大值以及最大单位
        const max = Math.max.apply(null, seriesObject.data)
        maxValue = max > maxValue ? max : maxValue
        const min = Math.min.apply(null, seriesObject.data)
        minValue = min > minValue ? min : minValue
        series.push(seriesObject)
      })
      setChartData({
        xData,
        series,
        maxValue,
        minValue
      })
    } else {
      setChartData(undefined)
    }
  }

  return (
    <Dialog
      mode="destroy"
      value={visible}
      width={1140}
      title={_$('监控详情')}
      slotFooter={
        <React.Fragment>
          <Button onClick={closeDetails}>关闭</Button>
        </React.Fragment>
      }
      onChangeValue={show => {
        if (!show) {
          closeDetails()
        }
      }}
    >
      <div className={className}>
        <div className="diabol-filter">
          <p>{_$('监控项')}：</p>
          <Select
            card
            width={200}
            value={monitorItemValue}
            onChangeValue={value => {
              setTags([])
              setMonitorItemValue(value)
            }}
          >
            {mointorClassData.length
              ? mointorClassData.map(({ appId, name, graphs }) => (
                  <OptionGroup label={name} key={appId}>
                    {graphs?.length
                      ? graphs.map(({ graphId, title }) => (
                          <Option key={`${graphId}-${title}`} value={`${graphId}-${title}`}>
                            {_$(title)}
                          </Option>
                        ))
                      : null}
                  </OptionGroup>
                ))
              : null}
          </Select>
          <div className="aggregate">
            <p>{_$('周期聚合方式')}：</p>
            <Select
              width={100}
              value={aggregate}
              onChangeValue={val => {
                setAggregate(val)
                getChartData({ aggregate: val })
              }}
            >
              {AggregatorEnum.map(item => (
                <Option key={item.value} value={item.value}>
                  {_$(item.label)}
                </Option>
              ))}
            </Select>
          </div>
          {appTags?.length ? (
            <div className="tag-select">
              <p>{_$('维度')}：</p>
              <Select
                style={{ width: 500 }}
                value={tags}
                multiple
                clearable
                nowrap
                placeholder={_$('默认全部维度')}
                onChangeValue={value => {
                  setTags(value)
                  getChartData({}, value)
                }}
              >
                {appTags.map(tag => (
                  <Option key={tag} value={tag}>
                    {tag}
                  </Option>
                ))}
              </Select>
            </div>
          ) : null}
        </div>
        <div className="diablo-date">
          <div className="quick-date">
            <p>{_$('时间范围')}：</p>
            <QuickRangeDate
              format="YYYY-MM-DD HH:mm"
              optionalDateRange={90}
              dateDiff={31}
              defautlValue={quickDate || rangeDate}
              changeValue={(date, quick) => {
                rangeDateChange(date, quick)
              }}
              quickRange={[
                '近5分钟',
                '近0.5小时',
                '近1小时',
                '近3小时',
                '近1天',
                '近3天',
                '近1周',
                '近30天'
              ]}
              clearable={false}
            />
          </div>
          <div className="delay">
            <p>{_$('周期')}：</p>
            <Select
              width={100}
              value={delayValue}
              onChangeValue={val => {
                setDelayValue(val)
                getChartData({ delay: val })
              }}
            >
              {delayOptions.map(item => (
                <Option value={item} key={item}>
                  {item}s
                </Option>
              ))}
            </Select>
          </div>
        </div>
        <div className="chart-box">
          {chartData?.xData?.length && chartData?.series?.length ? (
            <EchartReact
              option={{
                xAxis: {
                  type: 'category',
                  boundaryGap: false,
                  data: chartData.xData
                },
                series: chartData.series,
                yAxis: {
                  symbol: 'circle',
                  symbolSize: 4,
                  name: currentGraph.units,
                  nameTextStyle: {
                    align: 'left'
                  },
                  type: 'value',
                  axisLine: {
                    show: true
                  },
                  axisLabel: {
                    margin: 5,
                    formatter: function (value) {
                      if (value === 0) {
                        return 0
                      }
                      return handleUnit(value, currentGraph.units, true)
                    }
                  }
                },
                dataZoom:
                  chartData.xData?.length > 20
                    ? [
                        {
                          type: 'inside',
                          moveOnMouseMove: true,
                          start: 0,
                          end: 100
                        },
                        {
                          type: 'slider', // 滑动条缩放
                          xAxisIndex: [0, 1],
                          showDetail: false,
                          height: 20, // 固定高度
                          bottom: 10, // 距离底部10px
                          handleStyle: {
                            color: '#DEE5F8'
                          }
                        }
                      ]
                    : [],
                legend: {
                  right: '10px',
                  top: '7px',
                  type: 'scroll',
                  width: '80%'
                },
                tooltip: {
                  ...echartTooltipOptions,
                  formatter: function (params) {
                    return tooltipFormatter(params, currentGraph.units)
                  }
                },
                grid: {
                  top: 35,
                  left: 30,
                  right: 30,
                  bottom: 40,
                  containLabel: true
                }
              }}
            />
          ) : (
            <NoDataComponent />
          )}
        </div>
      </div>
    </Dialog>
  )
}

export default styled(DiabolChartDetails)`
  p {
    margin: 0;
  }
  .diabol-filter,
  .diablo-date {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    padding-bottom: 8px;
    & > div {
      display: flex;
      align-items: center;
      margin-right: 16px;
    }
  }
  .chart-box {
    width: 100%;
    height: 350px;
  }
`
